import React from 'react';
import { BrowserRouter, Navigate, Routes, Route, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ConfigProvider, App as AntApp, Spin } from 'antd';

import LeadOpsPage from './pages/LeadOpsPage';
import LoginPage from './pages/LoginPage';
import LeadOpsLayout from './components/Layout/LeadOpsLayout';
import { useAuth } from './hooks/useAuth';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      gcTime: 5 * 60 * 1000,
      retry: 1,
    },
  },
});

const AuthGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const { showLoginPage, isPending } = useAuth();

  // Background session checks must keep the workspace and its draft state mounted.
  if (isPending && !showLoginPage) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (showLoginPage) {
    if (location.pathname !== '/auth') {
      return <Navigate to={`/auth${location.search}`} replace />;
    }
    return <LoginPage />;
  }

  return <>{children}</>;
};

const LoginPageOrRedirect: React.FC = () => {
  const { isAuthenticated, isPending } = useAuth();
  if (isPending) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <Spin size="large" />
      </div>
    );
  }
  if (isAuthenticated) return <Navigate to="/" replace />;
  return <LoginPage />;
};

const App: React.FC = () => (
  <ConfigProvider>
    <AntApp>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <Routes>
            <Route path="/auth" element={<LoginPageOrRedirect />} />
            <Route
              path="/"
              element={
                <AuthGate>
                  <LeadOpsLayout>
                    <LeadOpsPage />
                  </LeadOpsLayout>
                </AuthGate>
              }
            />
          </Routes>
        </BrowserRouter>
      </QueryClientProvider>
    </AntApp>
  </ConfigProvider>
);

export default App;
